import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

/**
 * Textarea with @-mention autocomplete dropdown.
 *
 * Scope:
 *   - "internal" → search /api/internal/team-members and insert  @[Name](user:USER_ID)
 *   - "community" → search /api/community/aliases and insert @Dealer-XXXX
 *
 * Props:
 *   - value, onChange, placeholder, rows, maxLength, className, "data-testid"
 *   - autoFocus
 */
const MENTION_TOKEN_RE = /@([A-Za-z0-9_-]{0,32})$/;

export default function MentionTextarea({
  value = "",
  onChange,
  placeholder,
  scope = "community",
  rows = 5,
  maxLength = 8000,
  className = "",
  autoFocus = false,
  "data-testid": testid = "mention-textarea",
}) {
  const taRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const debounceRef = useRef(null);

  // Detect @-token under cursor
  const detect = (newVal, caretIdx) => {
    const slice = newVal.slice(0, caretIdx);
    const m = slice.match(MENTION_TOKEN_RE);
    if (m) {
      setQuery(m[1] || "");
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  // Fetch suggestions when query changes
  useEffect(() => {
    if (!open) {
      setResults([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        if (scope === "internal") {
          const r = await api.get(`/internal/team-members?q=${encodeURIComponent(query)}`);
          setResults(r.data.members || []);
        } else {
          const r = await api.get(`/community/aliases?q=${encodeURIComponent(query)}`);
          setResults(r.data.aliases || []);
        }
        setActive(0);
      } catch (e) {
        console.error("mention search failed", e);
        setResults([]);
      }
    }, 120);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, open, scope]);

  const insert = (item) => {
    const ta = taRef.current;
    if (!ta) return;
    const caret = ta.selectionStart || 0;
    const before = value.slice(0, caret);
    const after = value.slice(caret);
    const tokenStart = before.search(MENTION_TOKEN_RE);
    if (tokenStart === -1) return;
    const insertion = scope === "internal"
      ? `@[${item.name}](user:${item.user_id})`
      : `@${item.alias}`;
    const next = before.slice(0, tokenStart) + insertion + " " + after;
    onChange(next);
    setOpen(false);
    // Restore caret right after insertion
    requestAnimationFrame(() => {
      const newCaret = tokenStart + insertion.length + 1;
      ta.focus();
      ta.setSelectionRange(newCaret, newCaret);
    });
  };

  const onKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      insert(results[active]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  };

  const onInput = (e) => {
    const v = e.target.value;
    onChange(v);
    detect(v, e.target.selectionStart || v.length);
    // Approximate dropdown position below textarea
    const rect = e.target.getBoundingClientRect();
    setPos({ top: rect.bottom, left: rect.left });
  };

  return (
    <div className="relative">
      <textarea
        ref={taRef}
        value={value}
        onChange={onInput}
        onKeyDown={onKeyDown}
        onClick={(e) => detect(value, e.target.selectionStart || 0)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        rows={rows}
        maxLength={maxLength}
        autoFocus={autoFocus}
        className={className}
        data-testid={testid}
      />
      {open && results.length > 0 && (
        <div
          className="absolute left-0 right-0 top-full mt-1 z-50 bg-white border border-[#E5E7EB] rounded-lg shadow-lg max-h-64 overflow-y-auto"
          data-testid={`${testid}-mention-dropdown`}
        >
          {results.map((m, i) => (
            <button
              type="button"
              key={(m.user_id || m.alias) + i}
              onMouseDown={(e) => { e.preventDefault(); insert(m); }}
              className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                i === active ? "bg-fc-red/10" : "hover:bg-gray-50"
              }`}
              data-testid={`${testid}-mention-option-${i}`}
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-fc-red to-fc-navy flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0">
                {(m.name || m.alias || "?").slice(0, 2).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[14px] font-bold text-fc-navy truncate">
                  {scope === "internal" ? m.name : m.alias}
                </div>
                <div className="text-[11px] text-[#6B7280] truncate">
                  {scope === "internal"
                    ? (m.job_title || m.alias)
                    : (m.rank || "Rookie")}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
